import {
  type ActionResult,
  type InboxTab,
  LIVE_PERMISSIONS,
  type Post,
  type PostList,
  type Source,
} from "@brandwall/contracts";
import { useInfiniteQuery, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router";
import { ApiError, api, post as apiPost, engagement } from "../api";
import { LicensedSong } from "../components/LicensedSong";
import { LeavingSoon, PermissionUntil } from "../components/PermissionUntil";
import { PlayVideo } from "../components/PlayVideo";
import { LabelPicker, PostLabels, useLabelPosts } from "../components/PostLabels";
import { Reports } from "../components/Reports";
import { SavedVideo } from "../components/SavedVideo";
import { useWorkspace } from "../components/Shell";
import { useToast } from "../components/Toast";

const tabs: { id: InboxTab; label: string }[] = [
  { id: "to_review", label: "To review" },
  { id: "approved", label: "Approved" },
  { id: "skipped", label: "Skipped" },
  { id: "live", label: "Live" },
];

const sourceLabel: Record<Source, string> = {
  own: "Your post",
  tag: "Tagged you",
  mention: "Mentioned you",
  trusted: "Someone you trust",
  hashtag: "Your hashtag",
  manual: "Added by hand",
};

// Hashtag posts are in Discover (D-062); the Inbox is for posts about the brand.
const sources: (Source | "")[] = ["", "own", "tag", "mention", "trusted"];

function PermissionPill({ p }: { p: Post }) {
  if (p.permission === "own_post") return <span className="pill ok">✓ Your post</span>;
  if (p.permission === "trusted") return <span className="pill ok">✓ Trusted</span>;
  if (p.permission === "granted" || p.permission === "self_granted")
    return <span className="pill ok">✓ Said yes</span>;
  if (p.permission === "declined") return <span className="pill berry">Said no</span>;
  if (p.permission === "pending") return <span className="pill warn">Asked</span>;
  if (p.permission === "replied") return <span className="pill warn">Replied</span>;
  if (p.permission === "expired") return <span className="pill warn">No answer</span>;
  if (p.permission === "ended") return <span className="pill warn">Permission ended</span>;
  return <span className="pill warn">Needs permission</span>;
}

const FLAG_LABEL: Record<string, string> = {
  children: "May show children",
  competitor: "Competitor brand",
  screenshot: "Looks like a screenshot",
  dark: "Dark photo",
};

function asking(p: Post, ws: string | undefined) {
  const r = p.request;
  if (!r || p.live) return null;
  if (r.status === "draft") {
    return r.route === "comment" && !r.needsPerson ? (
      "Brandwall is asking by comment."
    ) : (
      <>
        {r.route === "comment" ? "The comment" : "A permission message"} is ready to send.{" "}
        <Link to={`/w/${ws}/rights?r=${r.id}`}>Send it</Link>
      </>
    );
  }
  if (r.status === "pending")
    return r.route === "comment" ? "Asked by comment." : "Message sent, waiting for an answer.";
  if (r.status === "replied")
    return (
      <>
        The creator replied, but not with your yes hashtag.{" "}
        <Link to={`/w/${ws}/rights?r=${r.id}`}>Read the reply</Link>
      </>
    );
  if (r.status === "expired")
    return (
      <>
        No answer after 7 days. <Link to={`/w/${ws}/rights?r=${r.id}`}>Ask once more</Link>
      </>
    );
  if (r.status === "declined" || r.status === "withdrawn") return "The creator said no.";
  return null;
}

export function Inbox() {
  const { ws } = useParams();
  const workspace = useWorkspace();
  const canAct = workspace.role !== "viewer";
  const labelPosts = useLabelPosts();
  // Posts whose creator's yes runs out within a week, or just did (D-099).
  const endingQ = useQuery({
    queryKey: ["ending", ws],
    queryFn: () => api<{ posts: Post[] }>(`/v1/w/${ws}/posts/ending?days=7&ended=true`),
  });
  const [params, setParams] = useSearchParams();
  const tab = (params.get("tab") as InboxTab) ?? "to_review";
  const source = (params.get("source") as Source | null) ?? "";
  const qc = useQueryClient();
  const toast = useToast();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [focus, setFocus] = useState(0);
  const [busy, setBusy] = useState(false);

  const query = useInfiniteQuery({
    queryKey: ["posts", ws, tab, source],
    initialPageParam: "",
    queryFn: ({ pageParam }) =>
      api<PostList>(
        `/v1/w/${ws}/posts?tab=${tab}${source ? `&source=${source}` : ""}${pageParam ? `&cursor=${pageParam}` : ""}`,
      ),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const posts = useMemo(() => query.data?.pages.flatMap((p) => p.posts) ?? [], [query.data]);
  const counts = query.data?.pages[0]?.counts;

  // biome-ignore lint/correctness/useExhaustiveDependencies: reset the selection when the tab or filter changes
  useEffect(() => {
    setSelected(new Set());
    setFocus(0);
  }, [tab, source]);

  const act = useCallback(
    async (path: string, ids: string[], body: Record<string, unknown> = {}) => {
      if (!ids.length || busy) return;
      setBusy(true);
      try {
        const r = await apiPost<ActionResult>(`/v1/w/${ws}/${path}`, { ids, ...body });
        toast(r.summary);
        setSelected(new Set());
        await qc.invalidateQueries({ queryKey: ["posts", ws] });
      } catch (err) {
        toast(err instanceof ApiError ? err.message : "Something went wrong.");
      } finally {
        setBusy(false);
      }
    },
    [busy, qc, toast, ws],
  );

  const single = async (path: string, value: boolean) => {
    try {
      const r = await apiPost<ActionResult>(`/v1/w/${ws}/${path}`, { value });
      toast(r.summary);
      await qc.invalidateQueries({ queryKey: ["posts", ws] });
    } catch (err) {
      toast(err instanceof ApiError ? err.message : "Something went wrong.");
    }
  };

  const block = async (username: string) => {
    if (
      !window.confirm(
        `Block @${username}? Their posts disappear from all your walls, and Brandwall stops collecting new ones.`,
      )
    )
      return;
    const r = await apiPost<ActionResult>(`/v1/w/${ws}/creators/block`, { username });
    toast(r.summary);
    await qc.invalidateQueries({ queryKey: ["posts", ws] });
  };

  // Keyboard: J/K or arrows to move, A to approve, S to skip, X to select.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement;
      if (t.closest("input, textarea, select") || e.metaKey || e.ctrlKey || e.altKey) return;
      const p = posts[focus];
      if (e.key === "j" || e.key === "ArrowRight")
        setFocus((f) => Math.min(posts.length - 1, f + 1));
      else if (e.key === "k" || e.key === "ArrowLeft") setFocus((f) => Math.max(0, f - 1));
      else if (!p || !canAct) return;
      else if (e.key === "a" && p.status !== "approved") act("posts/approve", [p.id]);
      else if (e.key === "s" && p.status !== "skipped") act("posts/skip", [p.id]);
      else if (e.key === "x")
        setSelected((s) => {
          const n = new Set(s);
          n.has(p.id) ? n.delete(p.id) : n.add(p.id);
          return n;
        });
      else return;
      e.preventDefault();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [posts, focus, act, canAct]);

  useEffect(() => {
    document.getElementById(`post-${posts[focus]?.id}`)?.scrollIntoView({ block: "nearest" });
  }, [focus, posts]);

  const set = (k: string, v: string) => {
    const n = new URLSearchParams(params);
    if (v) n.set(k, v);
    else n.delete(k);
    setParams(n, { replace: true });
  };

  return (
    <div>
      <Reports />
      <LeavingSoon posts={endingQ.data?.posts ?? []} canAct={canAct} />
      <div className="tabs" role="tablist">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={tab === t.id}
            className={`tab ${tab === t.id ? "on" : ""}`}
            onClick={() => set("tab", t.id === "to_review" ? "" : t.id)}
          >
            {t.label}
            {counts && counts[t.id] > 0 && <span className="c">{counts[t.id]}</span>}
          </button>
        ))}
      </div>
      <div className="row wrap" style={{ marginBottom: 16 }}>
        <select
          className="input"
          style={{ width: "auto" }}
          value={source}
          onChange={(e) => set("source", e.target.value)}
          aria-label="Show posts from"
        >
          {sources.map((s) => (
            <option key={s} value={s}>
              {s ? sourceLabel[s] : "All posts"}
            </option>
          ))}
        </select>
        <span className="spacer" />
        {canAct && posts.length > 0 && (
          <span className="small muted kbd-hints">
            <span className="kbd">A</span> approve · <span className="kbd">S</span> skip ·{" "}
            <span className="kbd">X</span> select · <span className="kbd">J</span>/
            <span className="kbd">K</span> move
          </span>
        )}
      </div>

      {query.isPending && <p className="muted">Loading posts…</p>}
      {query.isSuccess && posts.length === 0 && (
        <div className="card" style={{ textAlign: "center" }}>
          <h2>{tab === "to_review" ? "All caught up" : "Nothing here yet"}</h2>
          <p className="muted">
            {tab === "to_review"
              ? "New posts that tag or mention you show up here. Brandwall looks for new ones every 30 minutes."
              : tab === "approved"
                ? "Posts you approve wait here until their creator says yes."
                : tab === "skipped"
                  ? "Posts you skip go here. You can bring them back at any time."
                  : "Approved posts with permission show up here, ready for your walls."}
          </p>
        </div>
      )}

      <div className="posts">
        {posts.map((p, i) => (
          <article
            key={p.id}
            id={`post-${p.id}`}
            className={`post ${selected.has(p.id) ? "sel" : ""} ${i === focus ? "focus" : ""}`}
            onFocus={() => setFocus(i)}
            aria-label={`Post by @${p.creator}`}
          >
            <div className="img">
              {p.previewUrl ? (
                <img
                  src={p.previewUrl}
                  alt={
                    p.caption
                      ? `Post by @${p.creator}: ${p.caption.slice(0, 100)}`
                      : `Post by @${p.creator}`
                  }
                  loading="lazy"
                />
              ) : (
                <div className="noimg">
                  {p.permalink ? (
                    <a href={p.permalink} target="_blank" rel="noreferrer">
                      See it on Instagram
                    </a>
                  ) : (
                    "No picture"
                  )}
                </div>
              )}
              <PlayVideo post={p} />
              {p.score !== null && tab !== "skipped" && (
                <span
                  className="score"
                  title={
                    p.scores
                      ? `Quality ${p.scores.quality} · Product ${p.scores.product} · Brand fit ${p.scores.brandFit}`
                      : undefined
                  }
                >
                  {p.score}
                </span>
              )}
              {p.topPick && tab === "to_review" && <span className="top-pick">TOP PICK</span>}
              {canAct && (
                <input
                  type="checkbox"
                  className="check"
                  checked={selected.has(p.id)}
                  aria-label={`Select post by @${p.creator}`}
                  onChange={(e) =>
                    setSelected((s) => {
                      const n = new Set(s);
                      e.target.checked ? n.add(p.id) : n.delete(p.id);
                      return n;
                    })
                  }
                />
              )}
            </div>
            <div className="body">
              <div className="who" title={`@${p.creator}`}>
                @{p.creator}
              </div>
              <div className="src">
                {sourceLabel[p.source]}
                {p.postedAt
                  ? ` · ${new Date(p.postedAt).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}`
                  : ""}
                {p.permalink && (
                  <>
                    {" · "}
                    <a href={p.permalink} target="_blank" rel="noreferrer">
                      Instagram ↗
                    </a>
                  </>
                )}
                {workspace.showEngagement && engagement(p) && <div>{engagement(p)}</div>}
              </div>
              <div className="pills">
                <PermissionPill p={p} />
                <LicensedSong post={p} />
                {p.flags
                  .filter((f) => FLAG_LABEL[f])
                  .map((f) => (
                    <span key={f} className="pill berry">
                      {FLAG_LABEL[f]}
                    </span>
                  ))}
              </div>
              <SavedVideo post={p} />
              <PermissionUntil p={p} canAct={canAct} />
              <PostLabels p={p} canAct={canAct} />
              {p.caption && (
                <div className="cap" title={p.caption}>
                  {p.caption}
                </div>
              )}
              {p.scoreReason && tab === "to_review" && (
                <div className="why" title="Why Brandwall scored it like this">
                  {p.scoreReason}
                </div>
              )}
              {p.skipReason && (
                <div className="small">
                  <b>{p.autoSkipped ? "Skipped by Brandwall:" : "Skipped:"}</b> {p.skipReason}
                </div>
              )}
            </div>
            {canAct &&
              (tab === "to_review" ? (
                <div className="acts">
                  <button
                    type="button"
                    className="btn ok sm"
                    title={
                      LIVE_PERMISSIONS.includes(p.permission)
                        ? "Puts it on your walls now."
                        : `Asks @${p.creator || "the creator"} for permission. It goes on your walls once they say yes.`
                    }
                    onClick={() => act("posts/approve", [p.id])}
                  >
                    {LIVE_PERMISSIONS.includes(p.permission) ? "✓ Approve" : "✓ Approve and ask"}
                  </button>
                  <button
                    type="button"
                    className="btn secondary sm"
                    onClick={() => act("posts/skip", [p.id])}
                  >
                    Skip
                  </button>
                </div>
              ) : tab === "skipped" ? (
                <div className="acts">
                  <button
                    type="button"
                    className="btn secondary sm"
                    onClick={() => act("posts/restore", [p.id])}
                  >
                    Bring back
                  </button>
                </div>
              ) : tab === "approved" ? (
                <div className="state">
                  {p.hidden ? (
                    <span className="muted">Hidden from all your walls</span>
                  ) : (
                    <>
                      <b>Approved, waiting for permission</b>
                      <div className="muted">
                        {asking(p, ws) ?? "Shows on your walls once the creator says yes."}
                      </div>
                    </>
                  )}
                  <div className="row" style={{ marginTop: 8 }}>
                    {p.hidden && (
                      <button
                        type="button"
                        className="btn secondary sm"
                        onClick={() => single(`posts/${p.id}/hide`, false)}
                      >
                        Show again
                      </button>
                    )}
                    <button
                      type="button"
                      className="btn secondary sm"
                      onClick={() => act("posts/restore", [p.id])}
                    >
                      Back to inbox
                    </button>
                  </div>
                </div>
              ) : (
                <div className="acts">
                  <button
                    type="button"
                    className="btn secondary sm"
                    onClick={() => single(`posts/${p.id}/pin`, !p.pinned)}
                  >
                    {p.pinned ? "Unpin" : "Pin"}
                  </button>
                  <button
                    type="button"
                    className="btn secondary sm"
                    onClick={() => single(`posts/${p.id}/hide`, true)}
                  >
                    Hide
                  </button>
                  <button
                    type="button"
                    className="btn secondary sm"
                    onClick={() => block(p.creator)}
                  >
                    Block
                  </button>
                </div>
              ))}
          </article>
        ))}
      </div>

      {query.hasNextPage && (
        <div style={{ textAlign: "center", marginTop: 18 }}>
          <button
            type="button"
            className="btn secondary"
            onClick={() => query.fetchNextPage()}
            disabled={query.isFetchingNextPage}
          >
            Show more
          </button>
        </div>
      )}

      {selected.size > 0 && (
        <div className="bulk" role="region" aria-label="Selected posts">
          <b>{selected.size} selected</b>
          <span className="spacer" />
          <button type="button" className="btn secondary sm" onClick={() => setSelected(new Set())}>
            Clear
          </button>
          {tab !== "skipped" && (
            <button
              type="button"
              className="btn secondary sm"
              onClick={() => act("posts/skip", [...selected])}
            >
              Skip
            </button>
          )}
          {tab === "skipped" && (
            <button
              type="button"
              className="btn secondary sm"
              onClick={() => act("posts/restore", [...selected])}
            >
              Bring back
            </button>
          )}
          {/* Labels for every selected post at once (D-100). */}
          <LabelPicker
            label="Label them"
            onPick={(pick) =>
              void labelPosts(
                [...selected],
                pick.id ? { add: [pick.id] } : { newName: pick.newName },
              ).then((ok) => ok && setSelected(new Set()))
            }
          />
          {tab !== "approved" && tab !== "live" && (
            <button
              type="button"
              className="btn ok sm"
              onClick={() => act("posts/approve", [...selected])}
            >
              Approve {selected.size}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
